/**
 * @file        src/pages/platform/AddTenant.jsx
 * @description Register / edit a tenant's ALREADY-EXISTING database. The
 *              backend test-connects with these credentials before saving —
 *              a typo here is caught immediately, not at the tenant admin's
 *              first failed login. db_password is never pre-filled on edit
 *              (the GET never returns it); leave blank to keep the current
 *              one. Reused for /platform/tenants/new and .../:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-27
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-07-27 Claude — Initial (multi-tenant super admin platform).
 *   - 2026-07-30 Claude — Added the Supplier API Credentials panel.
 *   - 2026-08-03 Claude — Added the mandatory Backend Public URL / Storefront
 *                          URL fields; Brands + Payment Gateways panels.
 *   - 2026-08-04 Claude — Admin Portal Branding panel.
 *   - 2026-08-05 Claude — Suspend confirm + password gate.
 *   - 2026-08-10 Claude — Slimmed to the REGISTRATION form. The four onboarding
 *                          panels (Branding, Brands, Payment Gateways, Supplier
 *                          Credentials) moved to their own Tenant Management
 *                          sidebar pages (Tenant*Page.jsx, ?tenant=<id>); this
 *                          page now shows the go-live ReadinessChecklist in
 *                          their place, deep-linking each unfinished step.
 *                          Status is no longer an editable field here — a new
 *                          tenant is created 'pending' by the server, and both
 *                          transitions (Go Live, Suspend) are explicit gated
 *                          actions on the Tenant List.
 *   - 2026-09-17 Claude — DB password is now <PasswordInput>. A typo here
 *                          fails as a connection error at readiness time,
 *                          nowhere near the field that caused it.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import Field from './Field';
import ReadinessChecklist, { useReadiness } from './ReadinessChecklist';
import PasswordInput from '../../components/PasswordInput';

// Origin only — scheme + host (+ port). The booking backend appends its own
// paths to these, so a stored path would corrupt every payment callback URL.
const originSchema = z.string().trim().min(1, 'Required').max(255)
  .transform((v) => v.replace(/\/+$/, ''))
  .superRefine((v, ctx) => {
    let u;
    try { u = new URL(v); } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a full URL, e.g. https://neonholidays.com' });
      return;
    }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must start with http:// or https://' });
    }
    if (u.pathname !== '/' || u.search || u.hash) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Origin only — no path or query' });
    }
  });

const baseSchema = {
  name: z.string().trim().min(1, 'Required').max(120),
  domain: z.string().trim().toLowerCase().min(1, 'Required').max(190)
    .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/, 'Enter a bare domain, e.g. neonholidays.com'),
  db_host: z.string().trim().min(1, 'Required').max(190),
  db_port: z.coerce.number().int().positive(),
  db_user: z.string().trim().min(1, 'Required').max(120),
  db_name: z.string().trim().min(1, 'Required').max(120),
  backend_public_url: originSchema,
  frontend_url: originSchema,
};

const empty = {
  name: '', domain: '', db_host: '', db_port: 3306, db_user: '', db_password: '',
  db_name: '', backend_public_url: '', frontend_url: '',
};

/** Host of a URL string, or '' if it isn't parseable yet (mid-typing). */
function hostOf(url) {
  try { return new URL(String(url).trim()).hostname.toLowerCase(); } catch { return ''; }
}

const STATUS_TONE = { active: 'ok', pending: 'pend', suspended: 'rej' };

export default function AddTenant() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const schema = z.object({
    ...baseSchema,
    db_password: isEdit
      ? z.union([z.string().min(1, 'Min 1 char'), z.literal('')]).optional()
      : z.string().min(1, 'Required'),
  });

  const { data: existing, isLoading, isError } = useQuery({
    queryKey: qk.tenants.one(id),
    queryFn: () => get(`/tenants/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const t = existing.data;
      setForm({
        name: t.name || '', domain: t.domain || '', db_host: t.db_host || '',
        db_port: t.db_port || 3306, db_user: t.db_user || '', db_password: '',
        db_name: t.db_name || '',
        // Existing rows predate these columns and come back NULL. Seed them from
        // the domain so an edit-and-save fixes the row rather than re-rejecting
        // it — the admin still sees the value before committing.
        backend_public_url: t.backend_public_url || (t.domain ? `https://${t.domain}` : ''),
        frontend_url: t.frontend_url || (t.domain ? `https://${t.domain}` : ''),
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/tenants/${id}`, body) : post('/tenants', body)),
    onSuccess: (res) => {
      toast.ok(isEdit ? 'Tenant updated' : 'Tenant registered', `${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      // A freshly registered tenant goes straight to its own edit page rather
      // than back to the list: the readiness checklist below walks the rest of
      // the onboarding (brands, gateway, supplier key, branding).
      const newId = res?.data?.id;
      if (!isEdit && newId) return navigate(`/platform/tenants/${newId}/edit`);
    },
    onError: (err) => {
      const apiErr = err.response?.data?.error;
      if (apiErr?.fields) setErrors(apiErr.fields);
      toast.err('Could not save', apiErr?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    // Mirrors the server rule (tenants.service.js). The gateway posts the payment
    // result to Backend Public URL, and the host in that callback picks the
    // database the booking is written to — so a URL on someone else's host means
    // the customer pays here and the booking lands there. Caught inline rather
    // than as a round-trip error.
    if (hostOf(parsed.data.backend_public_url) !== parsed.data.domain) {
      return setErrors({
        backend_public_url: `Must be on this tenant's own domain (${parsed.data.domain}) — the payment callback's host selects the database.`,
      });
    }

    setErrors({});
    const body = { ...parsed.data };
    if (isEdit && !body.db_password) delete body.db_password;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Typing the domain fills both URLs, but only while they still hold the
  // generated default — a hand-edited value is never clobbered.
  function onDomain(e) {
    const next = e.target.value.trim().toLowerCase();
    setForm((f) => {
      const isDefault = (v) => !v || v === `https://${f.domain}`;
      const seeded = next ? `https://${next}` : '';
      return {
        ...f,
        domain: next,
        backend_public_url: isDefault(f.backend_public_url) ? seeded : f.backend_public_url,
        frontend_url: isDefault(f.frontend_url) ? seeded : f.frontend_url,
      };
    });
  }

  const status = existing?.data?.status;

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Tenant' : 'Add Tenant'}
        subtitle="Platform · Tenant Management"
        actions={<button className="btn ghost" onClick={() => navigate('/platform/tenants')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        {isEdit && isLoading ? (
          <div className="empty">Loading tenant…</div>
        ) : isEdit && isError ? (
          <div className="empty">
            <p className="field-error">Could not load this tenant.</p>
            <button className="btn ghost" onClick={() => navigate('/platform/tenants')}>Back to list</button>
          </div>
        ) : (
          <form className="form-grid" onSubmit={onSubmit}>
            <Field label="Tenant Name" required err={errors.name}>
              <input value={form.name} onChange={on('name')} placeholder="e.g. NeonHolidays" />
            </Field>
            <Field label="Domain" required err={errors.domain} hint="Bare hostname, no scheme/path, e.g. neonholidays.com">
              <input value={form.domain} onChange={onDomain} placeholder="neonholidays.com" />
            </Field>
            {isEdit && status ? (
              <Field label="Status" hint="Changed from the Tenant List — Go Live runs the readiness checks; Suspend asks for your password.">
                <div style={{ paddingTop: 6 }}>
                  <Chip tone={STATUS_TONE[status] || 'rej'}>{status}</Chip>
                </div>
              </Field>
            ) : null}

            <Field label="Backend Public URL" required err={errors.backend_public_url}
                   hint="Where the payment gateway posts its result. Must be on this tenant's own domain — its host selects the database the booking is written to.">
              <input value={form.backend_public_url} onChange={on('backend_public_url')}
                     placeholder="https://neonholidays.com" />
            </Field>
            <Field label="Storefront URL" required err={errors.frontend_url}
                   hint="Where the customer is returned after paying. Also allow-lists this storefront for the post-payment redirect.">
              <input value={form.frontend_url} onChange={on('frontend_url')}
                     placeholder="https://neonholidays.com" />
            </Field>

            <Field label="DB Host" required err={errors.db_host}>
              <input value={form.db_host} onChange={on('db_host')} placeholder="127.0.0.1" />
            </Field>
            <Field label="DB Port" required err={errors.db_port}>
              <input value={form.db_port} onChange={on('db_port')} placeholder="3306" />
            </Field>
            <Field label="DB Name" required err={errors.db_name}>
              <input value={form.db_name} onChange={on('db_name')} placeholder="tenant_database" />
            </Field>
            <Field label="DB User" required err={errors.db_user}>
              <input value={form.db_user} onChange={on('db_user')} placeholder="tenant_app" />
            </Field>
            <Field label="DB Password" required={!isEdit} err={errors.db_password}
                   hint={isEdit ? 'Leave blank to keep the current password.' : undefined}>
              <PasswordInput value={form.db_password} onChange={on('db_password')} placeholder="••••••••" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
            </Field>

            <div className="form-actions">
              <button className="btn primary" type="submit" disabled={mut.isPending}>
                <Icon name="plus" /> {mut.isPending ? 'Testing connection…' : isEdit ? 'Save Changes' : 'Register Tenant'}
              </button>
            </div>
          </form>
        )}
      </article>

      {/* Onboarding progress — replaces the four config panels that used to be
          stacked here (now their own Tenant Management pages). Every check
          deep-links to the page that completes it; the Tenant List's Go Live
          action is what finally flips status once all are green. */}
      {isEdit ? <OnboardingPanel tenantId={id} status={status} /> : null}
    </>
  );
}

function OnboardingPanel({ tenantId, status }) {
  const { data, isLoading, isError } = useReadiness(tenantId);
  return (
    <article className="panel glass" style={{ marginTop: 16 }}>
      <div className="panel-head">
        <div>
          <h2>Go-Live Readiness</h2>
          <p className="muted">
            {status === 'active'
              ? 'This tenant is live. The checks below should stay green — a red one means something was unconfigured after go-live.'
              : 'Everything below must pass before this tenant can go live (Tenant List → Go Live). Each unfinished step links to the page that completes it.'}
          </p>
        </div>
        {data ? (data.ready ? <Chip tone="ok">Ready</Chip> : <Chip tone="pend">Incomplete</Chip>) : null}
      </div>
      <ReadinessChecklist tenantId={tenantId} readiness={data} isLoading={isLoading} isError={isError} />
    </article>
  );
}
